import { Block, ImageWordpress } from '../../types/shared';
import { Seo } from '../seo-settings/seo-settings-types';

type LinkItem = {
    url: string;
    title: string;
    target?: string;
};

/**
 * A session is either a legacy (content-fallback) session or a DB-backed session.
 *
 * This MUST be a discriminated union: a single type with required
 * id / isFullyBooked / order fields cannot honestly represent an un-migrated
 * fallback session, which only carries the wall-clock times emitted by the content
 * sessions array. Discriminate on the presence of id via isDbSession.
 */
export type LegacySession = {
    startTime: string;
    endTime: string;
};

export type DbSession = {
    id: string;
    startTime: string;
    endTime: string;
    /** null = unlimited capacity; absent while still loading. Never the raw participantCount. */
    spotsLeft?: number | null;
    isFullyBooked: boolean;
    order: number;
};

export type Session = LegacySession | DbSession;

/** Narrowing guard: a DB-backed session is the only shape that carries an opaque id. */
export const isDbSession = (s: Session): s is DbSession => 'id' in s;

export type ListCardContent = {
    image: ImageWordpress;
    title: string;
    cardText: string;
    displayHostName: 'yes' | 'no';
    hostName: string;
};

export type EventMeta = {
    postTitle: string;
    description?: string;
    featuredImage?: ImageWordpress;
    categories?: Category[];
    extra?: {
        listCardContent?: ListCardContent;
        isVisibleForPublic?: boolean;
        isHealthcareProfessional?: boolean;
        date?: string;
        eventDate?: string;
        disableMirroringInCountries?: boolean;
        isOnDemand?: boolean;
        eventExpireDate?: string;
        eventLanguage?: string;
        eventTime?: string;
        hideFromGlobalEventsListing?: boolean;
        hideFromEventsListing?: boolean;
        areaOfCare?: string;
        tags?: string[];
        wordpressTags?: Tag[];
        eventLocation?: string;
        isFullyBooked?: boolean;
        url?: string;
        eventType?: 'goto-event' | 'in-person' | 'regular';
        isInPerson?: boolean;
        isSessionBased?: boolean;
        sessions?: Session[];
    };
};

export type EventListDataContainer = {
    key: string;
    meta?: EventMeta;
}

export type EventListCardData = {
    url: string;
    permalink: string;
    listCardContent?: Partial <ListCardContent>;
    categories?: Category[];
    isHealthcareProfessional?: boolean;
    isVisibleForPublic?: boolean;
    date?: string;
    eventDate?: string;
    disableMirroringInCountries?: boolean;
    isOnDemand?: boolean;
    eventExpireDate?: string;
    eventLanguage?: string;
    eventTime?: string;
    hideFromGlobalEventsListing?: boolean;
    hideFromEventsListing?: boolean;
    areaOfCare?: string | string[];
    tags?: string[];
    wordpressTags?: Tag[];
    eventLocation?: string;
    isFullyBooked?: boolean;
    eventType?: 'goto-event' | 'in-person' | 'regular';
    isInPerson?: boolean;
    isSessionBased?: boolean;
    sessions?: Session[];
}

export type EventData = {
    id: number;
    post_title: string;
    url: string;
    blocks: Block[];
    permalink: string;
    showRelatedEvents: boolean;
    relatedEventsPosition: 'sidebar' | 'bottom';
    date: string;
    author: string;
    categories: Category[];
    areaOfCare: string;
    wordpressTags: Tag[];
    eventExpireDate: string,
    listCardContent: ListCardContent;
    type: string;
    eventDate: string;
    eventTime: string;
    eventLocation: string;
    eventLanguage: string;
    eventRegistrationLink: LinkItem;
    isHealthcareProfessional: boolean;
    isVisibleForPublic: boolean;
    hideFromGlobalEventsListing: boolean;
    hideFromEventsListing: boolean;
    disableMirroringInCountries: boolean;
    tags: string[];
    seo: Seo;
    isFullyBooked: boolean;
    gotoEventId?: string;
    isOnDemand?: boolean;
    eventType?: 'goto-event' | 'in-person' | 'regular';
    isInPerson?: boolean;
    isSessionBased?: boolean;
    sessions?: Session[];
    externalId?: string;
    disableBreadcrumbs?: boolean;
    useBreadcrumbTitle?: boolean;
    breadcrumbTitle?: string;
    meta?: EventMeta;
    isUserContent?: boolean;
};

export type Category = {
    termId: number;
    name: string;
    slug: string;
    parent: number;
    children: Category[];
};

export type Tag = {
    termId: number;
    name: string;
    slug: string;
    parent: number;
    children: Tag[];
};

